import React, { useEffect, useState, useCallback } from "react";

import TopNavbar from "../../Components/TopNavbar/TopNavbar";
import { Helmet } from "react-helmet-async";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "../../Services/AxiosInstance";
import { getEmployeeDetailApi } from "../../Services/EmployeeOnboarding";

const CheckCircleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="9"
      cy="9"
      r="8.25"
      stroke="#22C55E"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M5.5 9L7.8 11.5L12.5 6.5"
      stroke="#22C55E"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6L12 2Z"
      stroke="#3B82F6"
      strokeWidth="1.8"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="#3B82F6"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2Z"
      stroke="#94A3B8"
      strokeWidth="1.8"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M14 2v6h6"
      stroke="#94A3B8"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M8 13h8M8 17h5"
      stroke="#94A3B8"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="#3B82F6"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Aadhar Modal ─────────────────────────────────────────────────────────────
const AadharModal = ({ open, onClose, onSuccess }) => {
  const [aadharId, setAadharId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async () => {
    const trimmed = aadharId.trim();
    if (!trimmed) {
      setError("Please enter your Aadhar number.");
      return;
    }
    if (!/^\d{12}$/.test(trimmed)) {
      setError("Aadhar number must be exactly 12 digits.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await api.post(
        "/api/employee-connect/employee/surepass/initiate_digilocker",
        { aadhar_id: trimmed },
      );

      const data = res?.data;

      if (data?.success) {
        onSuccess(data, trimmed);
      } else {
        setError(data?.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setAadharId("");
    setError("");
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{
          background: "rgba(15, 23, 42, 0.45)",
          backdropFilter: "blur(2px)",
          zIndex: "15000",
        }}
        onClick={handleClose}
      >
        {/* Modal Box */}
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full mx-4"
          style={{ maxWidth: "420px" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-7 pt-7 pb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center rounded-xl"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "#DBEAFE",
                  flexShrink: 0,
                }}
              >
                <ShieldIcon />
              </div>
              <div>
                <h3 className="text-[16px] font-bold text-[#1E293B] leading-tight">
                  Continue with Digilocker
                </h3>
                <p className="text-[12px] text-[#94A3B8] mt-0.5">
                  Securely verify your identity
                </p>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-[#94A3B8] hover:text-[#475569] transition-colors ml-3 mt-0.5 flex-shrink-0"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-7 py-6">
            <label className="block text-[13px] font-semibold text-[#374151] mb-2">
              Aadhar Number
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={12}
              placeholder="Enter 12-digit Aadhar number"
              value={aadharId}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setAadharId(val);
                if (error) setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              disabled={loading}
              className="w-full px-4 py-3 rounded-xl text-[14px] text-[#1E293B] placeholder-[#CBD5E1] outline-none transition-all"
              style={{
                border: error ? "1.5px solid #EF4444" : "1.5px solid #E2E8F0",
                background: "#F8FAFC",
              }}
              onFocus={(e) => {
                if (!error) e.target.style.border = "1.5px solid #3B82F6";
              }}
              onBlur={(e) => {
                if (!error) e.target.style.border = "1.5px solid #E2E8F0";
              }}
            />

            {/* Error */}
            {error && (
              <p className="mt-2 text-[12px] text-red-500 flex items-center gap-1">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle
                    cx="7"
                    cy="7"
                    r="6.25"
                    stroke="#EF4444"
                    strokeWidth="1.4"
                  />
                  <path
                    d="M7 4v4M7 10v.5"
                    stroke="#EF4444"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
                {error}
              </p>
            )}

            {/* Helper text */}
            {!error && (
              <p className="mt-2 text-[12px] text-[#94A3B8]">
                Your Aadhar details are encrypted and never stored.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-7 pb-7 flex gap-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-[13.5px] font-semibold text-[#475569] transition-colors hover:bg-slate-50"
              style={{ border: "1.5px solid #E2E8F0" }}
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading || aadharId.length !== 12}
              className="flex-1 py-2.5 rounded-xl text-[13.5px] font-semibold text-white transition-all flex items-center justify-center gap-2"
              style={{
                background:
                  loading || aadharId.length !== 12
                    ? "#93C5FD"
                    : "linear-gradient(135deg, #3B82F6, #2563EB)",
                cursor:
                  loading || aadharId.length !== 12 ? "not-allowed" : "pointer",
              }}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="3"
                      strokeOpacity="0.3"
                    />
                    <path
                      d="M12 2a10 10 0 0110 10"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                  Verifying...
                </>
              ) : (
                <>
                  Proceed
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const Welcome = () => {
  const [isNavbarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const getData = useCallback(async () => {
    const res = await getEmployeeDetailApi();
    console.log(
      "Employee details response:",
      res?.data?.data?.progress_bar?.personal_details,
    );
    if (res?.data?.success && res?.data?.data?.progress_bar?.personal_details) {
      navigate("/new-user/personal");
    }
  }, [navigate]);

  const handleDigilockerSuccess = (data, aadharId) => {
    const { client_id, sdk_url } = data?.data || {};

    // NOTE: This handler starts a Digilocker flow by saving the
    // `client_id` and `aadhar_id` to localStorage and redirecting the
    // browser to the provider's `sdk_url`. We intentionally preserve
    // this behavior when the user chooses the Digilocker option.
    //
    // The "Fill manually" action (below) no longer removes
    // `digilocker_data` from localStorage; instead it navigates with
    // { state: { digilocker_skip: true } } so the PersonalDetails
    // screen can skip any Digilocker polling while keeping the
    // stored data available if the user wants to resume later.

    if (client_id && aadharId) {
      localStorage.setItem(
        "digilocker_data",
        JSON.stringify({
          request_id: client_id,
          aadhar_id: aadharId,
        }),
      );
    }

    if (sdk_url) {
      window.location.href = sdk_url;
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <Helmet>
        <style>{`
          body { background: #EEF2F7; }
          
          @media (max-width: 768px) {
            #dashboard-body {
              margin: 0 !important;
            }
          }
        `}</style>
      </Helmet>

      {/* Aadhar Modal */}
      <AadharModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleDigilockerSuccess}
      />

      <div id="dashboard-body">
        <TopNavbar />

        {/* Centered content */}
        <div
          className="flex flex-col items-center justify-center px-4 md:px-0"
          style={{ height: "calc(100vh - 82px)" }}
        >
          {/* Heading */}
          <h1 className="mt-4 md:0 text-2xl md:text-[28px] font-bold text-[#1E293B] tracking-tight mb-2 text-center">
            Welcome, {localStorage.getItem("firstName") || "there"} !
          </h1>
          <p className="text-sm text-[#64748B] mb-8 md:mb-10 text-center">
            Let's get your details sorted. Choose how you'd like to proceed.
          </p>

          {/* Cards Row */}
          <div className="flex flex-col md:flex-row gap-5 items-stretch w-full md:max-w-[680px] max-w-full px-4 md:px-0">
            {/* ── Digilocker Card (highlighted) ── */}
            {/*
              Fill manually card
              - Navigates to PersonalDetails with a navigation state flag
                `digilocker_skip: true` which instructs the target page to
                skip Digilocker-related polling and surefetch behavior.
              - We do NOT remove `digilocker_data` here so any in-progress
                Digilocker request remains available if the user returns
                to the Digilocker flow later.
            */}
            <div
              className="rounded-2xl p-6 md:p-7 cursor-pointer flex-1 transition-all hover:shadow-lg w-full"
              style={{
                background: "#EBF5FF",
                border: "1.5px solid #BAD9F5",
              }}
              onClick={() => setModalOpen(true)}
            >
              {/* Icon */}
              <div
                className="flex items-center justify-center rounded-xl mb-5"
                style={{ width: "42px", height: "42px", background: "#DBEAFE" }}
              >
                <ShieldIcon />
              </div>

              {/* Title */}
              <h2 className="text-base md:text-[16.5px] font-bold text-[#1E293B] mb-2 leading-snug">
                Continue with Digilocker
              </h2>

              {/* Description */}
              <p className="text-[13px] text-[#64748B] leading-relaxed mb-4">
                Securely import your verified details. Takes under a minute.
              </p>

              {/* Feature bullets */}
              <div className="flex flex-col gap-[10px] mb-5">
                {[
                  "Auto-fill personal details",
                  "Verified documents",
                  "Skip manual entry",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircleIcon />
                    <span className="text-[13px] text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>

              {/* Recommended link */}
              <button
                className="flex items-center gap-1 text-[13px] font-semibold text-blue-500 bg-transparent border-0 p-0 cursor-pointer transition-opacity hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(true);
                }}
              >
                Recommended <ArrowRightIcon />
              </button>
            </div>

            {/* ── Fill Manually Card (dashed/inactive) ── */}
            <div
              className="rounded-2xl p-6 md:p-7 cursor-pointer bg-white flex-1 transition-all hover:shadow-lg w-full"
              style={{
                border: "1.5px dashed #CBD5E1",
              }}
              onClick={() => {
                navigate("/new-user/personal", { state: { digilocker_skip: true } });
              }}
            >
              {/* Icon */}
              <div
                className="flex items-center justify-center rounded-xl mb-5"
                style={{ width: "42px", height: "42px", background: "#F1F5F9" }}
              >
                <DocumentIcon />
              </div>

              {/* Title */}
              <h2 className="text-base md:text-[16.5px] font-bold text-[#1E293B] mb-2 leading-snug">
                Fill manually
              </h2>

              {/* Description */}
              <p className="text-[13px] text-[#64748B] leading-relaxed mb-3">
                Enter your details and upload documents yourself.
              </p>

              {/* Time estimate */}
              <p className="text-[13px] text-[#94A3B8]">Takes ~10 minutes</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Welcome;
